import type { AnalyticValueType } from '../types';

import { formatAnalyticData, hasMetricData, readNumeric } from '../helpers';
import { useConvertMoneyByCurrency } from '~/modules/shopify';
import { PLACEHOLDER_VALUE } from '../constants';

interface IFormatDataParams {
  value: AnalyticValueType;
  formatter?: string;
  name?: string;
}

type MetricRecord = Record<string, unknown>;
interface IComputeMetricParams {
  metric?: MetricRecord;
  previousMetric?: MetricRecord;
  metricKey: string;
  formatter?: string;
}

interface IComputeMetricResult {
  value: AnalyticValueType;
  change: number | string;
}

interface IUseAnalyticDataReturn {
  formatData: (params: IFormatDataParams) => AnalyticValueType;
  computeMetric: (params: IComputeMetricParams) => IComputeMetricResult;
}

export const useAnalyticData = (): IUseAnalyticDataReturn => {
  const { getTextPrice } = useConvertMoneyByCurrency();

  const formatData = ({ value, formatter, name }: IFormatDataParams): AnalyticValueType => {
    return formatAnalyticData({
      value,
      formatter,
      getTextPrice,
      name,
    });
  };

  const computeMetric = ({
    metric,
    previousMetric,
    metricKey,
    formatter,
  }: IComputeMetricParams): IComputeMetricResult => {
    if (!hasMetricData(metric)) return { value: 0, change: PLACEHOLDER_VALUE };

    const currentValue = readNumeric(metric, metricKey);
    const previousValue = readNumeric(previousMetric, metricKey);
    const value = formatData({ value: currentValue, formatter, name: metricKey });

    if (currentValue === 0 && previousValue !== 0) return { value, change: -100 };
    if (previousValue === 0) return { value, change: PLACEHOLDER_VALUE };

    const change = ((currentValue - previousValue) / previousValue) * 100;

    return { value, change };
  };

  return {
    formatData,
    computeMetric,
  };
};
